import { BaseQueryFn, createApi } from '@reduxjs/toolkit/query/react'
import axios, { AxiosError } from 'axios'
import { ApiError, AuthService, createAxiosInstance } from 'authy-react'

export const authService = new AuthService(import.meta.env.VITE_API_URL)

export const axiosInstance = createAxiosInstance(authService, {
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    'Content-type': 'application/json',
    'x-api-key': import.meta.env.VITE_API_KEY,
  },
  timeout: 50000,
})

export const publicAxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    'Content-type': 'application/json',
    'x-api-key': import.meta.env.VITE_API_KEY,
  },
  timeout: 50000,
})

const axiosBaseQuery: BaseQueryFn = async ({ url, method, data, params, headers }) => {
  try {
    const response = await axiosInstance.request({
      url,
      method,
      data,
      params,
      headers,
    })
    return { data: response }
  } catch (error) {
    if (error instanceof ApiError) {
      return { error }
    }
    if (error instanceof AxiosError && error.response) {
      const code = error.response.data.code || 'unknown_error'
      let message = error.response.data.message || 'Greška pri pozivu API-ja.'

      if (code === 'bot-disabled') {
        message = 'Izvinite, bot trenutno nije u mogućnosti da odgovara na pitanja.'
      }

      if (code === 'limit-exceeded') {
        message = 'Mesečni limit je dostignut. Bot trenutno ne može da odgovori na nova pitanja.'
      }

      return {
        error: {
          status: error.response.status,
          data: {
            code,
            message,
          },
        },
      }
    }
    return {
      error: {
        status: 'unknown',
        data: {
          code: 'unexpected_error',
          message: 'Došlo je do greške. Pokušajte ponovo kasnije.',
        },
      },
    }
  }
}

interface ApiErrorResponse {
  code?: string
  message?: string
}

const publicAxiosBaseQuery: BaseQueryFn = async ({ url, method, data, params, headers }) => {
  try {
    const res = await publicAxiosInstance.request({ url, method, data, params, headers })
    return { data: res.data }
  } catch (error) {
    const e = error as AxiosError<ApiErrorResponse>
    const code = e.response?.data?.code || 'unknown_error'
    let message = e.response?.data?.message || 'Greška pri pozivu API-ja.'
    if (code === 'bot-disabled') message = 'Izvinite, bot trenutno nije u mogućnosti da odgovara na pitanja.'
    if (code === 'limit-exceeded') {
      message = 'Mesečni limit je dostignut. Bot trenutno ne može da odgovori na nova pitanja.'
    }
    return { error: { status: e.response?.status ?? 'unknown', data: { code, message } } }
  }
}

export const api = createApi({
  reducerPath: 'api',
  baseQuery: axiosBaseQuery,
  /**
   * Tag types must be defined in the original API definition
   * for any tags that would be provided by injected endpoints
   */
  tagTypes: ['Chat', 'Question', 'Category'],
  /**
   * This api has endpoints injected in adjacent files.
   */
  endpoints: () => ({}),
})

export const publicApi = createApi({
  reducerPath: 'publicApi',
  baseQuery: publicAxiosBaseQuery,
  tagTypes: ['Chat'],
  endpoints: () => ({}),
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function apiError(error: any): {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: { status: number; message: string; code: string; data: any; args: any }
} {
  return {
    error: {
      status: error.status,
      code: error.code,
      data: error.data,
      args: error.args,
      message: error.message,
    },
  }
}
